/**
 * PDP description preview: readable slice, then expand in place.
 * Does not rely on custom element upgrade. Delegated click + enhance()
 * work even if Impact rehydrates / product-rerender replaces the node.
 * Desktop tracks the live sibling .product-gallery__ar-wrapper bottom.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === "object" && module.exports) {
    module.exports = api;
  }
  if (root) root.aerobisDescriptionPreview = api;
  if (typeof document !== "undefined") api.boot(root);
})(typeof window !== "undefined" ? window : globalThis, function () {
  var PREVIEW_MIN_PX = 288;
  var PREVIEW_CEILING_PX = 1600;
  /* If flush remaining is below this, use PREVIEW_MIN instead of a crushed clamp. */
  var FLUSH_MIN_SPACE_PX = 180;
  var DESKTOP_MQ = "(min-width: 1000px)";
  var OVERFLOW_SLACK_PX = 12;
  var PREVIEW_SEL = "product-description-preview, [data-description-preview]";
  var GALLERY_SEL = "product-gallery, .product-gallery";
  var SECTION_EVENTS = [
    "shopify:section:load",
    "shopify:section:reorder",
    "shopify:section:select",
    "product:rerender"
  ];

  var enhanced = typeof WeakSet === "function" ? new WeakSet() : null;
  var states = typeof WeakMap === "function" ? new WeakMap() : null;
  var booted = false;

  function previewCeilingPx(win) {
    var vh = win && win.innerHeight ? Number(win.innerHeight) : 0;
    if (!(vh > 0)) return PREVIEW_CEILING_PX;
    return Math.round(Math.min(Math.max(vh * 0.92, PREVIEW_MIN_PX), PREVIEW_CEILING_PX));
  }

  function previewFlushHeightPx(opts) {
    opts = opts || {};
    var galleryBottom = Number(opts.galleryBottom);
    var bodyTop = Number(opts.bodyTop);
    if (isFinite(galleryBottom) && isFinite(bodyTop) && (opts.galleryBottom != null) && (opts.bodyTop != null)) {
      var remaining = Math.round(galleryBottom - bodyTop);
      return remaining > 0 ? remaining : 0;
    }
    var galleryHeight = Number(opts.galleryHeight);
    return galleryHeight > 0 ? Math.round(galleryHeight) : 0;
  }

  function previewMaxHeightPx(opts) {
    opts = opts || {};
    var minPx = opts.minPx > 0 ? opts.minPx : PREVIEW_MIN_PX;
    if (!opts.desktop) return minPx;
    var raw = Number(opts.galleryHeight);
    var ceiling = Number(opts.ceilingPx);
    if (!(ceiling > 0)) ceiling = PREVIEW_CEILING_PX;
    /* Desktop flush: ar-wrapper.bottom - body.top.
       Tiny/zero remaining (stacked buy box) must not collapse to 0px. */
    if (opts.aligned) {
      var flushMin = typeof FLUSH_MIN_SPACE_PX === "number" ? FLUSH_MIN_SPACE_PX : 180;
      if (!(raw > 0) || raw < flushMin) return minPx;
      return Math.min(Math.round(raw), ceiling);
    }
    if (!(raw > 0)) return minPx;
    return Math.min(Math.round(raw), ceiling);
  }

  function isDesktopViewport(win) {
    win = win || (typeof window !== "undefined" ? window : null);
    return !!(win && win.matchMedia && win.matchMedia(DESKTOP_MQ).matches);
  }

  function closestScope(rootEl, selector) {
    if (!rootEl) return null;
    if (rootEl.closest) return rootEl.closest(selector);
    return null;
  }

  function findGallery(rootEl) {
    var scopes = [
      closestScope(rootEl, ".product"),
      closestScope(rootEl, "product-rerender"),
      closestScope(rootEl, ".shopify-section"),
      closestScope(rootEl, "[id^='shopify-section']")
    ];
    var i;
    for (i = 0; i < scopes.length; i++) {
      if (!scopes[i] || !scopes[i].querySelector) continue;
      var found = scopes[i].querySelector(GALLERY_SEL);
      if (found) return found;
    }
    if (typeof document !== "undefined" && document.querySelector) {
      return document.querySelector(GALLERY_SEL);
    }
    return null;
  }

  /* Main image card Elmar highlighted. Not the outer gallery / thumbnail column. */
  function findGalleryAlignNode(rootEl) {
    var gallery = findGallery(rootEl);
    if (!gallery) return null;
    if (gallery.matches && gallery.matches(".product-gallery__ar-wrapper")) return gallery;
    if (gallery.querySelector) {
      var card = gallery.querySelector(".product-gallery__ar-wrapper");
      if (card) return card;
    }
    return gallery;
  }

  function galleryObserveTargets(gallery) {
    if (!gallery) return [];
    var nodes = [gallery];
    if (gallery.querySelector) {
      nodes.push(
        gallery.querySelector(".product-gallery__ar-wrapper"),
        gallery.querySelector(".product-gallery__media-list-wrapper"),
        gallery.querySelector(".product-gallery__media-list"),
        gallery.querySelector("media-carousel"),
        gallery.querySelector(".product-gallery__thumbnail-list-wrapper"),
        gallery.querySelector(".product-gallery__thumbnail-list")
      );
    }
    var seen = [];
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i] && seen.indexOf(nodes[i]) === -1) seen.push(nodes[i]);
    }
    return seen;
  }

  function nodeHeightPx(node) {
    if (!node) return 0;
    var rectH = node.getBoundingClientRect ? node.getBoundingClientRect().height : 0;
    var offH = Number(node.offsetHeight) || 0;
    var clientH = Number(node.clientHeight) || 0;
    return Math.round(Math.max(rectH || 0, offH, clientH));
  }

  function galleryHeightPx(rootEl) {
    return nodeHeightPx(findGalleryAlignNode(rootEl));
  }

  function contentSource(body) {
    if (!body) return null;
    if (body.querySelector) {
      return body.querySelector("[data-description-content], .prose") || body.firstElementChild || body;
    }
    return body;
  }

  function contentHeightPx(body) {
    var source = contentSource(body);
    if (!source) return 0;
    return Math.max(Number(source.scrollHeight) || 0, Number(source.offsetHeight) || 0);
  }

  function contentOverflows(body, maxPx) {
    var height = contentHeightPx(body);
    if (!height) return false;
    return height > maxPx + OVERFLOW_SLACK_PX;
  }

  function previewEls(root) {
    var scope = root && root.querySelectorAll ? root : (typeof document !== "undefined" ? document : null);
    if (!scope) return [];
    if (scope.matches && scope.matches(PREVIEW_SEL)) return [scope];
    return Array.prototype.slice.call(scope.querySelectorAll(PREVIEW_SEL));
  }

  function getState(el) {
    var state = states ? states.get(el) : el.__aerobisPreview;
    if (!state) {
      state = {
        appliedMaxPx: 0,
        pending: false,
        observedGallery: null,
        observeTargets: [],
        resizeObserver: null,
        mutationObserver: null,
        desktopMq: null
      };
      if (states) states.set(el, state);
      else el.__aerobisPreview = state;
    }
    return state;
  }

  function bindLoads(root, onLoad) {
    if (!root || !root.querySelectorAll) return;
    var imgs = root.querySelectorAll("img");
    for (var j = 0; j < imgs.length; j++) {
      if (!imgs[j].complete) imgs[j].addEventListener("load", onLoad, { once: true });
    }
  }

  function unobserveGallery(el) {
    var state = getState(el);
    if (state.resizeObserver && state.observeTargets) {
      for (var i = 0; i < state.observeTargets.length; i++) {
        try { state.resizeObserver.unobserve(state.observeTargets[i]); } catch (err) {}
      }
    }
    if (state.mutationObserver) {
      try { state.mutationObserver.disconnect(); } catch (err2) {}
    }
    state.observedGallery = null;
    state.observeTargets = [];
  }

  function observeGallery(el) {
    var state = getState(el);
    var gallery = findGallery(el);
    if (!gallery || gallery === state.observedGallery) return;
    unobserveGallery(el);
    state.observedGallery = gallery;
    state.observeTargets = galleryObserveTargets(gallery);
    if (state.resizeObserver) {
      for (var i = 0; i < state.observeTargets.length; i++) {
        state.resizeObserver.observe(state.observeTargets[i]);
      }
    }
    if (typeof MutationObserver === "function") {
      state.mutationObserver = new MutationObserver(function () { scheduleSync(el); });
      state.mutationObserver.observe(gallery, {
        attributes: true,
        attributeFilter: ["style", "hidden", "class", "filtered-indexes"],
        childList: true,
        subtree: true
      });
    }
    bindLoads(gallery, function () { scheduleSync(el); });
  }

  function applyMaxHeight(el) {
    observeGallery(el);
    var state = getState(el);
    var desktop = isDesktopViewport(typeof window !== "undefined" ? window : null);
    var alignNode = findGalleryAlignNode(el);
    var galleryHeight = nodeHeightPx(alignNode);
    var measured = galleryHeight;
    var aligned = false;
    if (desktop && alignNode && el && el.querySelector) {
      var body = el.querySelector("[data-description-body]");
      if (body && alignNode.getBoundingClientRect && body.getBoundingClientRect) {
        var gRect = alignNode.getBoundingClientRect();
        var bRect = body.getBoundingClientRect();
        measured = previewFlushHeightPx({
          galleryBottom: gRect.bottom,
          bodyTop: bRect.top
        });
        aligned = true;
      }
    }
    var maxPx = previewMaxHeightPx({
      desktop: desktop,
      galleryHeight: measured,
      aligned: aligned,
      ceilingPx: previewCeilingPx(typeof window !== "undefined" ? window : null)
    });
    var matched = galleryHeight > 0;
    if (maxPx === state.appliedMaxPx && el.classList.contains("is-matched") === matched) {
      return maxPx;
    }
    state.appliedMaxPx = maxPx;
    el.style.setProperty("--description-preview-max", maxPx + "px");
    if (matched) {
      el.style.setProperty("--description-gallery-height", galleryHeight + "px");
      el.classList.add("is-matched");
    } else {
      el.style.removeProperty("--description-gallery-height");
      el.classList.remove("is-matched");
    }
    return maxPx;
  }

  function setToggleLabel(el, expanded) {
    var toggle = el.querySelector("[data-description-toggle]");
    if (!toggle) return;
    var label = toggle.querySelector("[data-description-toggle-label]");
    var expandLabel = toggle.getAttribute("data-expand") || (label ? label.textContent : "");
    var collapseLabel = toggle.getAttribute("data-collapse") || expandLabel;
    if (label) label.textContent = expanded ? collapseLabel : expandLabel;
  }

  function syncPreview(el) {
    if (!el || !el.querySelector) return;
    var body = el.querySelector("[data-description-body]");
    var toggle = el.querySelector("[data-description-toggle]");
    if (!body || !toggle) return;
    var maxPx = applyMaxHeight(el);
    var expandable = contentOverflows(body, maxPx);
    var expanded = el.classList.contains("is-expanded");
    el.classList.toggle("is-truncated", expandable);
    el.classList.add("is-ready");
    toggle.hidden = !expandable && !expanded;
    toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
    setToggleLabel(el, expanded);
  }

  function scheduleSync(el) {
    var state = getState(el);
    if (state.pending) return;
    state.pending = true;
    var run = function () {
      if (!state.pending) return;
      state.pending = false;
      syncPreview(el);
    };
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(run);
    setTimeout(run, 16);
  }

  function togglePreview(el) {
    if (!el) return false;
    enhance(el);
    var expanded = el.classList.toggle("is-expanded");
    var toggle = el.querySelector("[data-description-toggle]");
    if (toggle) {
      toggle.hidden = false;
      toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
    }
    setToggleLabel(el, expanded);
    syncPreview(el);
    if (!expanded) {
      var heading = el.querySelector(".product-info__description-title");
      var top = el.getBoundingClientRect ? el.getBoundingClientRect().top : 0;
      if (top < 12 && heading && heading.scrollIntoView) {
        heading.scrollIntoView({ block: "start" });
      }
      if (toggle && toggle.focus) toggle.focus();
    }
    return expanded;
  }

  function handleToggle(event) {
    if (!event || !event.target) return false;
    var target = event.target;
    if (target.nodeType === 3) target = target.parentElement;
    var toggle = target && target.closest ? target.closest("[data-description-toggle]") : null;
    if (!toggle) return false;
    var preview = toggle.closest(PREVIEW_SEL);
    if (!preview) return false;
    if (event.preventDefault) event.preventDefault();
    togglePreview(preview);
    return true;
  }

  function enhance(el) {
    if (!el || !el.querySelector) return el;
    var body = el.querySelector("[data-description-body]");
    var toggle = el.querySelector("[data-description-toggle]");
    if (!body || !toggle) return el;
    el.sync = function () { syncPreview(el); };
    if (enhanced && enhanced.has(el)) {
      scheduleSync(el);
      return el;
    }
    if (enhanced) enhanced.add(el);
    var state = getState(el);
    if (typeof ResizeObserver === "function" && !state.resizeObserver) {
      state.resizeObserver = new ResizeObserver(function () { scheduleSync(el); });
      state.resizeObserver.observe(body);
      var inner = contentSource(body);
      if (inner && inner !== body) state.resizeObserver.observe(inner);
    }
    if (typeof window !== "undefined" && window.matchMedia && !state.desktopMq) {
      state.desktopMq = window.matchMedia(DESKTOP_MQ);
      var onMq = function () { scheduleSync(el); };
      if (state.desktopMq.addEventListener) state.desktopMq.addEventListener("change", onMq);
      else if (state.desktopMq.addListener) state.desktopMq.addListener(onMq);
    }
    bindLoads(body, function () { scheduleSync(el); });
    observeGallery(el);
    syncPreview(el);
    return el;
  }

  function enhanceAll(root) {
    var nodes = previewEls(root);
    for (var i = 0; i < nodes.length; i++) enhance(nodes[i]);
    return nodes.length;
  }

  function defineElement() {
    if (typeof window === "undefined" || !window.customElements || typeof HTMLElement === "undefined") return;
    if (window.customElements.get("product-description-preview")) return;
    try {
      class ProductDescriptionPreview extends HTMLElement {
        connectedCallback() { enhance(this); }
        disconnectedCallback() { unobserveGallery(this); }
        sync() { syncPreview(this); }
      }
      window.customElements.define("product-description-preview", ProductDescriptionPreview);
    } catch (err) {
      /* Progressive enhance() still binds click and measure. */
    }
  }

  function onDocClick(event) {
    handleToggle(event);
  }

  function onSectionEvent(event) {
    var root = event && event.target && event.target.querySelectorAll ? event.target : null;
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(function () { enhanceAll(root || document); });
    }
    setTimeout(function () { enhanceAll(root || document); }, 50);
  }

  function boot(win) {
    if (typeof document === "undefined") return;
    if (booted) {
      enhanceAll(document);
      return;
    }
    booted = true;
    defineElement();
    document.addEventListener("click", onDocClick, false);
    document.addEventListener("DOMContentLoaded", function () { enhanceAll(document); });
    if (document.readyState !== "loading") enhanceAll(document);
    if (win) {
      win.addEventListener("resize", function () { enhanceAll(document); });
      win.addEventListener("pageshow", function () { enhanceAll(document); });
      win.addEventListener("load", function () { enhanceAll(document); });
    }
    for (var i = 0; i < SECTION_EVENTS.length; i++) {
      document.addEventListener(SECTION_EVENTS[i], onSectionEvent);
    }
    if (typeof MutationObserver === "function") {
      var mo = new MutationObserver(function (mutations) {
        for (var m = 0; m < mutations.length; m++) {
          var added = mutations[m].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (!n || n.nodeType !== 1) continue;
            if ((n.matches && n.matches(PREVIEW_SEL)) || (n.querySelector && n.querySelector(PREVIEW_SEL))) {
              enhanceAll(n);
              return;
            }
          }
        }
      });
      var startMo = function () {
        if (document.documentElement) {
          mo.observe(document.documentElement, { childList: true, subtree: true });
        }
      };
      if (document.documentElement) startMo();
      else document.addEventListener("DOMContentLoaded", startMo);
    }
  }

  return {
    PREVIEW_MIN_PX: PREVIEW_MIN_PX,
    PREVIEW_CEILING_PX: PREVIEW_CEILING_PX,
    DESKTOP_MQ: DESKTOP_MQ,
    PREVIEW_SEL: PREVIEW_SEL,
    GALLERY_SEL: GALLERY_SEL,
    previewMaxHeightPx: previewMaxHeightPx,
    previewFlushHeightPx: previewFlushHeightPx,
    previewCeilingPx: previewCeilingPx,
    isDesktopViewport: isDesktopViewport,
    findGallery: findGallery,
    findGalleryAlignNode: findGalleryAlignNode,
    galleryObserveTargets: galleryObserveTargets,
    galleryHeightPx: galleryHeightPx,
    contentHeightPx: contentHeightPx,
    contentOverflows: contentOverflows,
    enhance: enhance,
    enhanceAll: enhanceAll,
    sync: syncPreview,
    toggle: togglePreview,
    handleToggle: handleToggle,
    defineElement: defineElement,
    boot: boot
  };
});
